import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { FixedSizeList as List } from 'react-window';

const ITEM_HEIGHT = 32;
const MAX_VISIBLE_ITEMS = 8;

const VirtualizedMultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = "Select...",
  isDisabled = false,
  maxSelection = 50
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Create a Set of selected values for O(1) lookup
  const selectedSet = useMemo(() => {
    return new Set(value.map(v => v.value));
  }, [value]);

  // Filter options based on search - memoized
  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    const search = searchText.toLowerCase();
    return options.filter(opt =>
      opt.label.toLowerCase().indexOf(search) !== -1
    );
  }, [options, searchText]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = useCallback((option) => {
    const isSelected = selectedSet.has(option.value);
    let newValue;

    if (isSelected) {
      newValue = value.filter(v => v.value !== option.value);
    } else {
      if (value.length >= maxSelection) {
        alert(`You can select maximum of ${maxSelection} items.`);
        return;
      }
      newValue = [...value, option];
    }
    onChange(newValue);
  }, [value, selectedSet, onChange, maxSelection]);

  const handleSelectAll = useCallback(() => {
    const itemsToSelect = filteredOptions.slice(0, maxSelection);
    onChange(itemsToSelect);
  }, [filteredOptions, onChange, maxSelection]);

  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const Row = useCallback(({ index, style }) => {
    const option = filteredOptions[index];
    const isSelected = selectedSet.has(option.value);

    return (
      <div
        style={{
          ...style,
          display: 'flex',
          alignItems: 'center',
          padding: '0 10px',
          cursor: 'pointer',
          backgroundColor: isSelected ? '#e6f3ff' : 'white',
          borderBottom: '1px solid #eee'
        }}
        onClick={() => handleToggle(option)}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => {}}
          style={{ marginRight: 8 }}
        />
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontSize: '13px'
        }}>
          {option.label}
        </span>
      </div>
    );
  }, [filteredOptions, selectedSet, handleToggle]);

  const listHeight = Math.min(filteredOptions.length * ITEM_HEIGHT, MAX_VISIBLE_ITEMS * ITEM_HEIGHT);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
        style={{
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          backgroundColor: isDisabled ? '#f5f5f5' : 'white',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          minHeight: '38px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <span style={{ color: value.length ? '#333' : '#999' }}>
          {value.length ? `${value.length} selected` : placeholder}
        </span>
        <span style={{ fontSize: '12px' }}>▼</span>
      </div>

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1000,
            marginTop: '2px'
          }}
        >
          <div style={{ padding: '8px', borderBottom: '1px solid #eee' }}>
            <input
              ref={inputRef}
              type="text"
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                padding: '6px 10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '13px'
              }}
              autoFocus
            />
          </div>

          <div style={{
            padding: '5px 10px',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '12px'
          }}>
            <span
              onClick={handleSelectAll}
              style={{ color: '#007bff', cursor: 'pointer' }}
            >
              Select All (max {maxSelection})
            </span>
            <span
              onClick={handleClearAll}
              style={{ color: '#dc3545', cursor: 'pointer' }}
            >
              Clear All
            </span>
          </div>

          {filteredOptions.length > 0 ? (
            <List
              height={listHeight}
              itemCount={filteredOptions.length}
              itemSize={ITEM_HEIGHT}
              width="100%"
            >
              {Row}
            </List>
          ) : (
            <div style={{ padding: '10px', textAlign: 'center', color: '#999' }}>
              No options found
            </div>
          )}

          <div style={{
            padding: '8px 10px',
            borderTop: '1px solid #eee',
            fontSize: '12px',
            color: '#666',
            textAlign: 'right'
          }}>
            {filteredOptions.length} items | {value.length} selected
          </div>
        </div>
      )}
    </div>
  );
};

export default VirtualizedMultiSelect;
